import { useParams } from "react-router-dom";
import {
  useGetDevice,
  useGetDocumentApproversQuery,
  useGetDocuments,
  useGetDocumentsTemplates,
  useGetUser,
  useGetUserById,
} from "../../../../../hooks";
import {
  Document,
  DocumentVersion,
  DocumentVersionApprover,
} from "../../../../../stores/models";
import { getDocVersionRevisionNumber } from "../../../../../utils";
import ApproverContent from "./ApproverContent";
import ApproversTabContentLoading from "./ApproversTabContentLoading";
import AuthorContent from "./authorContent/AuthorContent";
import CompleteScreen from "./CompleteScreen";
import { NotInvolvedContent } from "./NotInvolvedContent";

const requiredNumberOfApprovers = 2;

export const ApproversTabContent = ({
  selectedDocVersion,
  selectedDoc,
  deviceId,
  generatedDoc,
}: {
  selectedDoc: Document;
  selectedDocVersion: DocumentVersion;
  deviceId: string;
  templateKey: string;
  generatedDoc?: string;
}) => {
  const { data: user } = useGetUser();
  const { data: docVersionAuthor } = useGetUserById({
    userId: selectedDocVersion.createdBy,
  });

  const { orgId = "" } = useParams();
  const { data: templatesConfig } = useGetDocumentsTemplates({
    orgId,
    deviceId,
  });
  const { data: documents } = useGetDocuments(orgId, deviceId, templatesConfig);
  const { data: device } = useGetDevice({ orgId, deviceId });

  const { data: approvers } = useGetDocumentApproversQuery({
    orgId,
    documentId: selectedDocVersion.documentId,
    versionId: selectedDocVersion.id,
  });

  if (!selectedDocVersion) {
    return <ApproversTabContentLoading />;
  }

  const currentUserApprover = approvers?.find(
    (approver) => approver.userId === user?.id
  );

  let approver: DocumentVersionApprover | null = null;

  if (currentUserApprover) {
    approver = currentUserApprover;
  }

  if (
    !user ||
    approvers === undefined ||
    !docVersionAuthor ||
    !documents ||
    !device
  ) {
    return <ApproversTabContentLoading />;
  }

  const userIsAuthor = selectedDocVersion.createdBy === user.id;

  if (approvers && selectedDocVersion.revision !== null) {
    return (
      <CompleteScreen
        approvers={approvers}
        author={docVersionAuthor}
        docVersion={selectedDocVersion}
        revision={getDocVersionRevisionNumber(
          selectedDocVersion,
          selectedDoc.versions
        )}
        revisionSummary={selectedDocVersion.revisionSummary}
      />
    );
  }

  if (!approver && !userIsAuthor) {
    return (
      <NotInvolvedContent
        approvers={approvers}
        docVersionAuthor={docVersionAuthor}
        requiredNumberOfApprovers={requiredNumberOfApprovers}
      />
    );
  }

  if (approver && !userIsAuthor) {
    return (
      <ApproverContent
        approvers={approvers}
        revisionSummary={selectedDocVersion.revisionSummary}
        selectedDocVersion={selectedDocVersion}
        approver={approver}
      />
    );
  }

  if (userIsAuthor) {
    return (
      <AuthorContent
        author={docVersionAuthor}
        isDemoDevice={!!device?.isDemoDevice}
        generatedDoc={generatedDoc}
        selectedDocVersion={selectedDocVersion}
        selectedDoc={selectedDoc}
        docId={selectedDoc.id}
      />
    );
  }
};

export default ApproversTabContent;
